import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Package, Menu as MenuIcon, MapPin, Printer, Sparkles, Settings, Layers } from 'lucide-react';
import { AdminNavbar } from '../../components/custom/AdminNavbar';
import { useAuth } from '../../context/AuthContext';
import { adminAPI } from '../../utils/api';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!user || !user.is_admin) {
      navigate('/admin/login');
      return;
    }
    fetchDashboard();
  }, [user]);

  const fetchDashboard = async () => {
    try {
      const response = await adminAPI.getDashboard();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
      toast.error('Failed to load dashboard');
    }
  };

  if (!user || !user.is_admin) return null;

  return (
    <div className="min-h-screen bg-background">
      <AdminNavbar title="Dashboard" />

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <h2 className="text-3xl font-bold mb-6">Dashboard Overview</h2>


        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Link to="/admin/orders">
            <motion.div
              whileHover={{ y: -4 }}
              className="bg-white rounded-2xl p-6 shadow-sm cursor-pointer hover:shadow-lg transition-shadow"
            >
              <Package className="h-12 w-12 text-primary mb-4" />
              <h3 className="text-xl font-bold mb-2">Manage Orders</h3>
              <p className="text-muted-foreground">View and update order status</p>
            </motion.div>
          </Link>

          <Link to="/admin/menu">
            <motion.div
              whileHover={{ y: -4 }}
              className="bg-white rounded-2xl p-6 shadow-sm cursor-pointer hover:shadow-lg transition-shadow"
            >
              <MenuIcon className="h-12 w-12 text-primary mb-4" />
              <h3 className="text-xl font-bold mb-2">Manage Menu</h3>
              <p className="text-muted-foreground">Add, edit, or remove menu items</p>
            </motion.div>
          </Link>

          <Link to="/admin/delivery-postcodes">
            <motion.div
              whileHover={{ y: -4 }}
              className="bg-white rounded-2xl p-6 shadow-sm cursor-pointer hover:shadow-lg transition-shadow"
            >
              <MapPin className="h-12 w-12 text-primary mb-4" />
              <h3 className="text-xl font-bold mb-2">Delivery Postcodes</h3>
              <p className="text-muted-foreground">Manage delivery areas & fees</p>
            </motion.div>
          </Link>

          <Link to="/admin/printer-settings">
            <motion.div
              whileHover={{ y: -4 }}
              className="bg-white rounded-2xl p-6 shadow-sm cursor-pointer hover:shadow-lg transition-shadow"
            >
              <Printer className="h-12 w-12 text-primary mb-4" />
              <h3 className="text-xl font-bold mb-2">Printer Settings</h3>
              <p className="text-muted-foreground">Configure receipt printing</p>
            </motion.div>
          </Link>

          <Link to="/admin/suggested-products">
            <motion.div
              whileHover={{ y: -4 }}
              className="bg-white rounded-2xl p-6 shadow-sm cursor-pointer hover:shadow-lg transition-shadow"
              data-testid="suggested-products-link"
            >
              <Sparkles className="h-12 w-12 text-primary mb-4" />
              <h3 className="text-xl font-bold mb-2">Suggested Products</h3>
              <p className="text-muted-foreground">Manage upsell products</p>
            </motion.div>
          </Link>

          <Link to="/admin/settings">
            <motion.div
              whileHover={{ y: -4 }}
              className="bg-white rounded-2xl p-6 shadow-sm cursor-pointer hover:shadow-lg transition-shadow"
              data-testid="admin-settings-link"
            >
              <Settings className="h-12 w-12 text-primary mb-4" />
              <h3 className="text-xl font-bold mb-2">Settings</h3>
              <p className="text-muted-foreground">Configure hours & printer</p>
            </motion.div>
          </Link>

          <Link to="/admin/modifier-groups">
            <motion.div
              whileHover={{ y: -4 }}
              className="bg-white rounded-2xl p-6 shadow-sm cursor-pointer hover:shadow-lg transition-shadow"
              data-testid="modifier-groups-link"
            >
              <Layers className="h-12 w-12 text-primary mb-4" />
              <h3 className="text-xl font-bold mb-2">Modifier Groups</h3>
              <p className="text-muted-foreground">Reusable sauces, salad, extras</p>
            </motion.div>
          </Link>
        </div>

        {stats && stats.recent_orders.length > 0 && (
          <div>
            <h3 className="text-2xl font-bold mb-4">Recent Orders</h3>
            {/* Desktop / tablet: table */}
            <div className="hidden md:block bg-white rounded-2xl shadow-sm overflow-hidden">
              <table className="w-full">
                <thead className="bg-secondary/50">
                  <tr>
                    <th className="text-left p-4 font-semibold">Order ID</th>
                    <th className="text-left p-4 font-semibold">Customer</th>
                    <th className="text-left p-4 font-semibold">Amount</th>
                    <th className="text-left p-4 font-semibold">Status</th>
                    <th className="text-left p-4 font-semibold">Type</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recent_orders.map(order => (
                    <tr key={order.id} className="border-t">
                      <td className="p-4 font-mono text-sm">{order.id.slice(0, 8)}</td>
                      <td className="p-4">{order.customer_name}</td>
                      <td className="p-4 font-bold text-primary">£{order.total_amount.toFixed(2)}</td>
                      <td className="p-4">
                        <span className="px-3 py-1 text-xs font-medium rounded-full bg-primary/10 text-primary capitalize">
                          {order.order_status}
                        </span>
                      </td>
                      <td className="p-4 capitalize">{order.order_type}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile / POS: card list — no horizontal scroll */}
            <div className="md:hidden space-y-2">
              {stats.recent_orders.map(order => (
                <Link
                  key={order.id}
                  to="/admin/orders"
                  className="block rounded-xl border border-stone-200 bg-white p-4 shadow-sm"
                  data-testid={`dashboard-recent-${order.id}`}
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="font-mono text-sm font-bold">#{order.id.slice(0, 6).toUpperCase()}</span>
                    <span className="text-base font-bold text-primary">£{order.total_amount.toFixed(2)}</span>
                  </div>
                  <div className="mt-1 text-sm text-stone-700 truncate">{order.customer_name}</div>
                  <div className="mt-1.5 flex items-center gap-2 text-xs text-stone-500">
                    <span className="rounded-full bg-primary/10 px-2 py-0.5 font-medium capitalize text-primary">
                      {order.order_status}
                    </span>
                    <span className="capitalize">{order.order_type}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
